import { generators, validators } from '@playcastdotio/Messaging';
import type { GameSaveCampaignConfiguration, PlaycastGameSaveMessages, PlaycastMessage } from '@playcastdotio/Messaging';
import type {
  GameSaveCampaign, GameSaveError, LoadGameResult, SaveGameResult,
} from '../../managers/game-save/IGameSaveManager';
import type { IGameSaveNativeExecutor, NativeGameSaveOptions } from '../../managers/game-save/GameSaveManager';
import type { IIpcService } from '../ipc/IIpcService';
import type { IGameSaveReplyChannel } from './GameSaveReplyChannel';
import type {
  IGameSaveConfigurationAvailability, GameSaveConfigurationAvailabilityResult,
} from './IGameSaveConfigurationAvailability';

type GameSaveAction = 'saveGame' | 'loadGame' | 'checkGameSaveConfiguration';
type GameSaveServiceResult = SaveGameResult | LoadGameResult | GameSaveConfigurationAvailabilityResult;
interface PendingGameSaveRequest {
  action: GameSaveAction;
  finish(result: GameSaveServiceResult): void;
  timer: ReturnType<typeof setTimeout>;
}
export interface GameSaveMessageServiceDependencies {
  ipcService: IIpcService;
  replyChannel: IGameSaveReplyChannel;
  hostIdentity: { getHostAuthId(): string };
  operationTimeoutMilliseconds?: number;
  configurationTimeoutMilliseconds?: number;
  configurationProvider?: {
    readCampaignConfiguration(campaign: GameSaveCampaign): GameSaveCampaignConfiguration | undefined;
  };
}

/** Correlated native process calls; no game files, processes, paths, or archive code run here. */
export class GameSaveMessageService implements IGameSaveNativeExecutor, IGameSaveConfigurationAvailability {
  private readonly pending = new Map<string, PendingGameSaveRequest>();
  private readonly unsubscribe: () => void;
  private destroyed = false;
  constructor(private readonly dependencies: GameSaveMessageServiceDependencies) {
    this.unsubscribe = dependencies.replyChannel.subscribeGameSaveReplies((message) => this.receiveReply(message));
  }

  executeSaveGame(options: NativeGameSaveOptions): Promise<SaveGameResult> {
    return this.sendRequest('saveGame', options) as Promise<SaveGameResult>;
  }
  executeLoadGame(options: NativeGameSaveOptions): Promise<LoadGameResult> {
    return this.sendRequest('loadGame', options) as Promise<LoadGameResult>;
  }
  hasGameSaveConfiguration(campaign: GameSaveCampaign): Promise<GameSaveConfigurationAvailabilityResult> {
    try {
      const configuration = this.dependencies.configurationProvider?.readCampaignConfiguration(campaign);
      return this.sendRequest('checkGameSaveConfiguration', configuration === undefined ? { campaign }
        : { campaign, configuration }) as Promise<GameSaveConfigurationAvailabilityResult>;
    } catch {
      return Promise.resolve({ status: 'unavailable', error: {
        code: 'invalid_target', retryable: false, message: 'Assigned campaign game save settings are invalid',
      } });
    }
  }

  /** Cancel frontend waits on shutdown. Native operations retain their own safety gate until completion. */
  destroy(): void {
    this.destroyed = true;
    this.unsubscribe();
    for (const [requestId, request] of this.pending) {
      clearTimeout(request.timer);
      request.finish(this.failure(request.action, {
        code: 'internal', retryable: true, message: 'Game save messaging service stopped',
      }));
      this.pending.delete(requestId);
    }
  }

  private sendRequest(action: GameSaveAction,
    options: { campaign: GameSaveCampaign; configuration?: GameSaveCampaignConfiguration } | NativeGameSaveOptions):
    Promise<GameSaveServiceResult> {
    if (this.destroyed || !this.dependencies.replyChannel.isAvailable()) {
      return Promise.resolve(this.failure(action, {
        code: 'internal', retryable: true, message: 'Native game save transport is unavailable',
      }));
    }
    let requestId: string;
    let message: PlaycastMessage<PlaycastGameSaveMessages>;
    try {
      requestId = crypto.randomUUID();
      const hostId = this.dependencies.hostIdentity.getHostAuthId();
      message = action === 'checkGameSaveConfiguration'
      ? generators.gameSave.checkGameSaveConfiguration(hostId, options.campaign, requestId, options.configuration)
      : action === 'saveGame'
        ? generators.gameSave.saveGame(hostId, { ...options as NativeGameSaveOptions, requestId })
        : generators.gameSave.loadGame(hostId, { ...options as NativeGameSaveOptions, requestId });
    } catch {
      return Promise.resolve(this.failure(action, {
        code: 'internal', retryable: true, message: 'Native game save transport is unavailable',
      }));
    }
    if (!validators.gameSave.validateGameSaveMessage(message)) {
      return Promise.resolve(this.failure(action, {
        code: 'invalid_target', retryable: false, message: 'Game save request identity is invalid',
      }));
    }
    const timeout = action === 'checkGameSaveConfiguration'
      ? this.dependencies.configurationTimeoutMilliseconds ?? 10_000
      : this.dependencies.operationTimeoutMilliseconds ?? 420_000;
    return new Promise((finish) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        finish(this.failure(action, {
          code: 'deadline_exceeded', retryable: true, message: 'Native game save reply timed out',
        }));
      }, timeout);
      this.pending.set(requestId, { action, finish, timer });
      try { this.dependencies.ipcService.send(message); }
      catch {
        clearTimeout(timer);
        this.pending.delete(requestId);
        finish(this.failure(action, {
          code: 'transfer_failed', retryable: true, message: 'Native game save request could not be sent',
        }));
      }
    });
  }

  private receiveReply(message: unknown): void {
    const candidate = message as PlaycastMessage<PlaycastGameSaveMessages> | null;
    if (!candidate?.header?.isReply || candidate.header.target !== 'process' ||
      candidate.header.source !== 'playjector' || typeof candidate.body?.message?.requestId !== 'string') return;
    const requestId = candidate.body.message.requestId;
    const pending = this.pending.get(requestId);
    if (!pending || pending.action !== candidate.header.action) return;
    clearTimeout(pending.timer);
    this.pending.delete(requestId);
    const reply = validators.gameSave.validateGameSaveMessage(message);
    pending.finish(reply ? this.replyResult(reply) : this.failure(pending.action, {
      code: 'internal', retryable: false, message: 'Native game save reply is invalid',
    }));
  }

  private replyResult(reply: PlaycastMessage<PlaycastGameSaveMessages>): GameSaveServiceResult {
    const body = reply.body.message;
    if ('result' in body) return body.result;
    if ('configured' in body) {
      return body.error ? { status: 'unavailable', error: body.error }
        : { status: body.configured ? 'configured' : 'unconfigured' };
    }
    return this.failure(reply.header.action, {
      code: 'internal', retryable: false, message: 'Native game save reply is invalid',
    });
  }

  private failure(action: GameSaveAction, error: GameSaveError): GameSaveServiceResult {
    return action === 'checkGameSaveConfiguration' ? { status: 'unavailable', error }
      : { status: 'failed', error };
  }
}
